import { orgBillingTransferRequestContract } from "@common/contracts/org-billing-transfer-request.contract";
import { OrgBillingTransferCandidates } from "@common/dto/billing/org-billing-transfer-request.dto";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { queryClient } from "queryClient";
import { workspaceQueryKeys } from "./workspace.api";

const orgBillingTransferRequestClient = getBackendAPIClient(
    orgBillingTransferRequestContract,
);

interface CandidatesParams {
    organizationId: string;
    clientId: string;
}

type InitiateOrgBillingTransferRequestPayload = ServerInferRequest<
    typeof orgBillingTransferRequestContract.initiate
>["body"];

interface CancelOrgBillingTransferRequestParams {
    requestId: string;
    organizationId: string;
}

export const orgBillingTransferRequestQueryKeys = {
    all: () => ["org-billing-transfer-request"],
    candidates: ({ organizationId, clientId }: CandidatesParams) => [
        ...orgBillingTransferRequestQueryKeys.all(),
        "candidates",
        organizationId,
        clientId,
    ],
};

export function useOrgBillingTransferCandidatesQuery(params: CandidatesParams) {
    return useQuery({
        queryKey: orgBillingTransferRequestQueryKeys.candidates(params),
        queryFn: async (): Promise<OrgBillingTransferCandidates> => {
            const response =
                await orgBillingTransferRequestClient.getCandidates({
                    query: params,
                });
            if (response.status !== 200) {
                throw new Error("Failed to load billing transfer candidates");
            }
            return response.body;
        },
        gcTime: 0,
    });
}

export function useInitiateOrgBillingTransferRequestMutation() {
    return useMutation({
        mutationFn: async (
            params: InitiateOrgBillingTransferRequestPayload,
        ) => {
            const response = await orgBillingTransferRequestClient.initiate({
                body: params,
            });
            if (response.status !== 201) {
                throw new Error("Failed to send billing transfer request");
            }
            return response.body;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
        },
    });
}

export function useCancelOrgBillingTransferRequestMutation() {
    return useMutation({
        mutationFn: async ({
            requestId,
            organizationId,
        }: CancelOrgBillingTransferRequestParams) => {
            const response = await orgBillingTransferRequestClient.cancel({
                params: { requestId },
                body: { organizationId },
            });
            if (response.status !== 200) {
                throw new Error("Failed to cancel billing transfer request");
            }
            return response.body;
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
        },
    });
}
